import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Link,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Download,
  Visibility,
  Image
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { downloadService } from '../../services/downloadService';

const DJDownloads = () => {
  const [selectedDownload, setSelectedDownload] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Buscar downloads ativos
  const { data: downloadsData, isLoading, error } = useQuery({
    queryKey: ['activeDownloads'],
    queryFn: () => downloadService.getActiveDownloads(),
  });

  const downloads = downloadsData?.data?.downloads || [];

  const handleOpenModal = (download) => {
    setSelectedDownload(download);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedDownload(null);
  };

  // Função para tornar links clicáveis na descrição
  const renderDescriptionWithLinks = (description) => {
    if (!description) return '';
    
    // Regex para detectar URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = description.split(urlRegex);
    
    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        return (
          <Link
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: '#00D4FF',
              textDecoration: 'underline',
              '&:hover': {
                color: '#FF6B35'
              }
            }}
          >
            {part}
          </Link>
        );
      }
      return part;
    });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress sx={{ color: '#FF6B35' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">
          Erro ao carregar downloads: {error.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" mb={3}>
            <Download sx={{ color: '#FF6B35', mr: 2, fontSize: 32 }} />
            <Typography variant="h5" sx={{ fontFamily: 'Orbitron', fontWeight: 700, color: '#FF6B35' }}>
              Downloads Disponíveis
            </Typography>
          </Box>

          {downloads.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="h6" sx={{ opacity: 0.7, mb: 2 }}>
                Nenhum download disponível no momento
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.5 }}>
                Os downloads serão exibidos aqui quando estiverem disponíveis
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ bgcolor: 'rgba(10, 10, 10, 0.5)' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Imagem</TableCell>
                    <TableCell>Título</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {downloads
                    .sort((a, b) => a.order_position - b.order_position)
                    .map((download) => (
                    <TableRow 
                      key={download.id}
                      sx={{
                        '&:hover': {
                          bgcolor: 'rgba(255, 107, 53, 0.1)'
                        }
                      }}
                    >
                      <TableCell>
                        {download.image_url ? (
                          <Avatar
                            src={download.image_url}
                            variant="rounded"
                            sx={{ width: 60, height: 60 }}
                          >
                            <Image />
                          </Avatar>
                        ) : (
                          <Avatar
                            variant="rounded"
                            sx={{ width: 60, height: 60, bgcolor: '#FF6B35' }}
                          >
                            <Download />
                          </Avatar>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            color: '#FF6B35'
                          }}
                        >
                          {download.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            opacity: 0.8,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: 1.4,
                            maxWidth: 300
                          }}
                        >
                          {download.description?.substring(0, 150)}
                          {download.description?.length > 150 ? '...' : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label="Disponível"
                          color="success"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Ver Detalhes">
                          <IconButton
                            onClick={() => handleOpenModal(download)}
                            sx={{
                              color: '#00D4FF',
                              '&:hover': {
                                bgcolor: 'rgba(0, 212, 255, 0.1)',
                                color: '#FF6B35'
                              }
                            }}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Modal de Detalhes */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(10, 10, 10, 0.95)',
            border: '1px solid rgba(255, 107, 53, 0.3)'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Download sx={{ color: '#FF6B35', mr: 2 }} />
            <Typography variant="h6" sx={{ color: '#FF6B35', fontWeight: 600 }}>
              {selectedDownload?.title}
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {selectedDownload && (
            <Box>
              {/* Imagem do Download */}
              {selectedDownload.image_url && (
                <Box textAlign="center" mb={3}>
                  <img
                    src={selectedDownload.image_url}
                    alt={selectedDownload.title}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '300px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      border: '2px solid rgba(255, 107, 53, 0.3)'
                    }}
                  />
                </Box>
              )}

              {/* Descrição com links clicáveis */}
              <Typography
                variant="body1"
                sx={{
                  lineHeight: 1.6,
                  color: 'rgba(255, 255, 255, 0.9)',
                  whiteSpace: 'pre-wrap'
                }}
              >
                {renderDescriptionWithLinks(selectedDownload.description)}
              </Typography>

              {/* Informações adicionais */}
              <Box mt={3} p={2} sx={{ bgcolor: 'rgba(255, 107, 53, 0.1)', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  <strong>Data de criação:</strong> {new Date(selectedDownload.created_at).toLocaleDateString('pt-BR')}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button
            onClick={handleCloseModal}
            sx={{
              color: '#FF6B35',
              '&:hover': {
                bgcolor: 'rgba(255, 107, 53, 0.1)'
              }
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DJDownloads;
